import { useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import type {
  AddMessageInput,
  GeneratedBaseMessage,
  Message,
  NavigationMessage,
  DynamicFormMessage,
  SelectMessage,
  SystemMessage,
  TextMessage,
  UpdateMessage,
} from "@/types/agent/message";
import { agentStore } from "../store/agentStore";

/**
 * Custom hook for managing chat messages with React state
 * @returns Messages state and mutation functions
 */
export const useMessageHistory = () => {
  const router = useRouter();
  const [messages, setMessages] = useState<Message[]>(() => []);
  const pendingToolTracesRef = useRef<Record<string, unknown>[]>([]);

  // Clear Message
  const clearMessages = useCallback(() => {
    pendingToolTracesRef.current = [];
    setMessages([]);
  }, []);

  // Remove Message
  const removeMessage = useCallback((id: string) => {
    setMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  // Update Message
  const updateMessage = useCallback(
    (id: string, updatedFields: UpdateMessage) => {
      setMessages((prev) =>
        prev.map((message) => {
          if (message.id !== id) {
            return message;
          }
          const nextMessage: Message = { ...message };
          const fields = updatedFields as Record<string, unknown>;
          Object.entries(fields).forEach(([key, value]) => {
            if (value !== undefined) {
              (nextMessage as unknown as Record<string, unknown>)[key] = value;
            }
          });
          return nextMessage;
        })
      );
    },
    []
  );

  const generateBaseMessage = useCallback((): GeneratedBaseMessage => {
    const id = crypto.randomUUID();
    return {
      id,
      timestamp: Date.now(),
      delete: () => removeMessage(id),
      update: (updatedFields) => updateMessage(id, updatedFields),
    };
  }, [removeMessage, updateMessage]);

  const createMessageWithBase = useCallback(
    (message: AddMessageInput): Message => {
      const baseMessage = generateBaseMessage();
      return { ...message, ...baseMessage } as Message;
    },
    [generateBaseMessage]
  );

  // Get Message
  const getMessageById = useCallback(
    (id: string) => {
      return messages.find((message) => message.id === id);
    },
    [messages]
  );

  // Add Message
  const addMessage = useCallback(
    (message: AddMessageInput) => {
      const messageWithBase = createMessageWithBase(message);
      setMessages((prev) => [...prev, messageWithBase]);
      return messageWithBase;
    },
    [createMessageWithBase]
  );

  const addTextMessage = useCallback(
    (sender: Message["sender"], text: string, attachment?: string) => {
      const pendingToolTraces =
        sender === "assistant" ? pendingToolTracesRef.current : [];
      const newMessage = createMessageWithBase({
        sender,
        type: "text",
        content: text,
        attachment,
        toolTraces: pendingToolTraces,
      }) as TextMessage;
      if (sender === "assistant" && pendingToolTraces.length > 0) {
        pendingToolTracesRef.current = [];
      }
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    [createMessageWithBase]
  );

  const addSystemMessage = useCallback(
    (text: string) => {
      const newMessage = createMessageWithBase({
        sender: "assistant",
        type: "system",
        content: text,
      }) as SystemMessage;
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    [createMessageWithBase]
  );

  const addNavigationMessage = useCallback(
    (
      message: Partial<
        Pick<
          NavigationMessage,
          "text" | "url" | "backText" | "backUrl" | "duration" | "isComplete"
        >
      > & {
        url: string;
      }
    ) => {
      const input: AddMessageInput = {
        type: "navigate",
        sender: "assistant",
        text: message.text ?? "Navigiere zu " + message.url,
        url: message.url,
        backText: message.backText ?? "Zurück zur Startseite",
        backUrl: message.backUrl ?? "/",
        duration: message.duration ?? 1500,
        isComplete: message.isComplete ?? false,
        navigateTo: (url: string) => {
          try {
            const isExternal = /^https?:\/\//.test(url);
            if (isExternal) {
              window.open(url, "_blank", "noopener,noreferrer");
            } else {
              router.push(url);
              // On mobile the panel covers the whole viewport, so the page
              // change would be invisible behind it — collapse to a bottom
              // sheet (MWR-609). Every real navigation funnels through here
              // (countdown, back button, navigate_and_scroll); an aborted
              // countdown never does. Inert on lg+.
              agentStore.collapse();
            }
          } catch {
            window.location.href = url;
          }
        },
        abortNavigation: (messageId: string) => {
          updateMessage(messageId, { isComplete: true } as UpdateMessage);
        },
      };
      const newMessage = createMessageWithBase(input) as NavigationMessage;
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    [createMessageWithBase, router, updateMessage]
  );

  const getLastMessage = useCallback(() => {
    return messages[messages.length - 1];
  }, [messages]);

  const appendToolTracesToLastTextMessage = useCallback(
    (traces: Record<string, unknown>[]) => {
      if (traces.length === 0) {
        return;
      }

      setMessages((prev) => {
        let lastAssistantTextIndex = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          const message = prev[i];
          if (message.type === "text" && message.sender === "assistant") {
            lastAssistantTextIndex = i;
            break;
          }
        }

        if (lastAssistantTextIndex < 0) {
          pendingToolTracesRef.current = [
            ...pendingToolTracesRef.current,
            ...traces,
          ];
          return prev;
        }

        const lastMessage = prev[lastAssistantTextIndex] as TextMessage;
        const next = [...prev];
        next[lastAssistantTextIndex] = {
          ...lastMessage,
          toolTraces: [...(lastMessage.toolTraces ?? []), ...traces],
        };
        return next;
      });
    },
    []
  );

  const getConversationHistory = useCallback(() => {
    const result: {
      role: "user" | "assistant";
      content: string;
      toolTraces: Record<string, unknown>[];
    }[] = [];
    messages.forEach((message) => {
      switch (message.type) {
        case "text":
          result.push({
            role: message.sender,
            content: message.content,
            toolTraces: message.toolTraces ?? [],
          });
          break;
        case "button":
          result.push({
            role: "assistant",
            content: `[Button: ${message.text}]`,
            toolTraces: [],
          });
          break;
        case "dynamic-form":
          if (message.isSubmitted) {
            const formId = message.formId;
            let submitKey: string = formId;
            switch (formId) {
              case "application_form":
                submitKey = "job_application";
                break;
              case "lead_contact_form":
                submitKey = "lead_capture";
                break;
              case "document_upload_form":
                submitKey = "job_documents";
                break;
            }
            result.push({
              role: "user",
              content: `[Form Submitted: ${submitKey}]\n${JSON.stringify(message.submittedData)}`,
              toolTraces: [],
            });
          }
          break;
        case "select":
          if (message.isSelected && message.selectedValue) {
            const selected = message.options.find(
              (opt) => opt.value === message.selectedValue
            );
            result.push({
              role: "user",
              content: `[Terminwunsch: ${selected?.text ?? message.selectedValue}]`,
              toolTraces: [],
            });
          }
          break;
        default:
          break;
      }
    });
    return result;
  }, [messages]);

  const addDynamicFormMessage = useCallback(
    (options: {
      formId: DynamicFormMessage["formId"];
      title: string;
      description: string;
      fields: DynamicFormMessage["fields"];
      submitText: string;
      onSubmit?: DynamicFormMessage["onSubmit"];
    }) => {
      const input: AddMessageInput = {
        type: "dynamic-form",
        sender: "assistant",
        formId: options.formId,
        title: options.title,
        description: options.description,
        fields: options.fields,
        submitText: options.submitText,
        isSubmitted: false,
        onSubmit: options.onSubmit,
      };
      const newMessage = createMessageWithBase(input) as DynamicFormMessage;
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    [createMessageWithBase]
  );

  const addSelectMessage = useCallback(
    (options: {
      title: string;
      description?: string;
      options: SelectMessage["options"];
      onSelect?: SelectMessage["onSelect"];
    }) => {
      const input: AddMessageInput = {
        type: "select",
        sender: "assistant",
        title: options.title,
        description: options.description ?? "",
        options: options.options,
        isSelected: false,
        onSelect: options.onSelect,
      };
      const newMessage = createMessageWithBase(input) as SelectMessage;
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    [createMessageWithBase]
  );

  return {
    addMessage,
    addTextMessage,
    addSystemMessage,
    addNavigationMessage,
    addDynamicFormMessage,
    addSelectMessage,
    messages,
    updateMessage,
    removeMessage,
    clearMessages,
    getLastMessage,
    appendToolTracesToLastTextMessage,
    getMessageById,
    getConversationHistory,
  };
};
